/**
 * Date utilities for parsing and formatting dates from the backend.
 *
 * The backend stores all timestamps as timezone-unaware UTC datetimes.
 * We need to parse them as UTC and display in the user's local timezone.
 */

/**
 * Parse a UTC date string from the backend into a local Date object.
 * The backend sends ISO format strings without timezone info (e.g., "2024-01-15T10:30:00").
 * We append "Z" to indicate UTC so JavaScript parses it correctly.
 */
export function parseUTCDate(dateString: string): Date {
  // If the string already has timezone info, parse as-is
  if (dateString.endsWith('Z') || dateString.includes('+') || dateString.includes('-', 10)) {
    return new Date(dateString)
  }
  // Append Z to indicate UTC
  return new Date(dateString + 'Z')
}

/**
 * Format a UTC date string as a relative time (e.g., "5m ago", "2h ago").
 * Falls back to localized date string for older dates.
 */
export function formatRelativeDate(dateString: string): string {
  const date = parseUTCDate(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

/**
 * Format a UTC date string as a full localized datetime string.
 * Uses the user's locale for formatting.
 */
export function formatDateTime(dateString: string): string {
  const date = parseUTCDate(dateString)
  return date.toLocaleString()
}

/**
 * Format a UTC date string as a localized date only (no time).
 */
export function formatDate(dateString: string): string {
  const date = parseUTCDate(dateString)
  return date.toLocaleDateString()
}

/**
 * Format a UTC date string as a localized time only (no date).
 */
export function formatTime(dateString: string): string {
  const date = parseUTCDate(dateString)
  return date.toLocaleTimeString()
}

/**
 * Calculate and format the duration between two UTC date strings.
 * Returns "-" if endIso is null.
 */
export function formatDuration(startIso: string, endIso: string | null): string {
  if (!endIso) return '-'
  const start = parseUTCDate(startIso).getTime()
  const end = parseUTCDate(endIso).getTime()
  const diffMs = end - start
  if (diffMs < 1000) return `${diffMs}ms`
  if (diffMs < 60000) return `${(diffMs / 1000).toFixed(1)}s`
  return `${(diffMs / 60000).toFixed(1)}m`
}

/**
 * Format a duration with more precision (for event details).
 */
export function formatDurationPrecise(startIso: string, endIso: string | null): string {
  if (!endIso) return '-'
  const start = parseUTCDate(startIso).getTime()
  const end = parseUTCDate(endIso).getTime()
  const diffMs = end - start
  if (diffMs < 1000) return `${diffMs}ms`
  if (diffMs < 60000) return `${(diffMs / 1000).toFixed(2)}s`
  return `${(diffMs / 60000).toFixed(2)}m`
}
